import '../styles/form_styles.css';
import 'survey-core/survey-core.min.css';
import 'survey-core/i18n/russian';

import {Model} from 'survey-core';
import 'survey-js-ui';
import MarkdownIt from 'markdown-it';

import {themeJson} from './apply/_formStyles.js';
import {initIframeResize} from './_inIFrame.js';
import {
  getTrackingData,
  updateTrackingData,
  userBrowserId,
} from '../scripts/common.js';
import {
  applyTrackingDataToSurvey,
  applySurveyJsonLocalizationFallbacks,
  buildSurveyClientContext,
  buildSurveySubmitPayload,
  clearSurveyState,
  collectTrackingPayload,
  createSurveyMarkdownRenderer,
  createTrackingBridge,
  getLocalizedSurveyText,
  hasAnyTrackingData,
  loadSurveyState,
  normalizeSurveyLocale,
  resolveSurveyLocale,
  renderSurveyPrefix,
  saveSurveyState,
} from './_surveys_helpers.js';
import {runSurveyPostSubmitTracking} from './_surveys_post_submit_tracking.js';

const markdownRenderer = createSurveyMarkdownRenderer(MarkdownIt);

function setStatus(container, message, isError = false) {
  if (!container) {
    return;
  }
  container.textContent = message || '';
  container.className = isError ? 'text-sm text-red-700' : 'text-sm text-slate-600';
}

function getStorage() {
  if (typeof window === 'undefined') {
    return null;
  }
  return window.localStorage;
}

function bindSurveyFileUploads(survey) {
  survey.onUploadFiles.add((surveyInstance, options) => {
    const formData = new FormData();
    formData.append('userBrowserId', userBrowserId);
    options.files.forEach((file) => {
      formData.append('file', file);
    });

    fetch(`${BACKEND_URL}/api/forms/upload`, {
      method: 'POST',
      body: formData,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Upload failed with status ${response.status}`);
        }
        return response.json();
      })
      .then((data) => {
        const successData = options.files.map((file, index) => ({
          file,
          content: `${BACKEND_URL}${data.urls[index]}`,
        }));
        options.callback('success', successData);
      })
      .catch((error) => {
        console.error('Survey upload failed', error);
        options.callback('error');
      });
  });

  survey.onClearFiles.add((surveyInstance, options) => {
    options.callback('success');
  });
}

export async function createSurveyIn(formContainer, slug, options = {}) {
  if (!formContainer) {
    throw new Error('Survey container is required');
  }
  if (!slug) {
    setStatus(options.statusContainer, 'Survey was not specified. Use a valid survey slug.', true);
    return null;
  }

  setStatus(options.statusContainer, 'Loading survey...');

  let surveyResponse;
  try {
    const response = await fetch(`${BACKEND_URL}/api/surveys/${encodeURIComponent(slug)}`);
    if (!response.ok) {
      throw new Error(`Survey fetch failed with status ${response.status}`);
    }
    surveyResponse = await response.json();
  } catch (error) {
    console.error('Failed to load survey', error);
    setStatus(options.statusContainer, 'Survey was not found or could not be loaded.', true);
    return null;
  }

  const surveyItem = surveyResponse?.item;
  if (!surveyItem?.survey_json || typeof surveyItem.survey_json !== 'object') {
    setStatus(options.statusContainer, 'Survey payload is invalid.', true);
    return null;
  }

  const surveyLocale = options.lang || resolveSurveyLocale(window);
  const normalizedSurveyLocale = normalizeSurveyLocale(surveyLocale);
  const preparedSurveyJson = applySurveyJsonLocalizationFallbacks(surveyItem.survey_json);
  if (options.titleNode) {
    options.titleNode.textContent = getLocalizedSurveyText(preparedSurveyJson.title, normalizedSurveyLocale) || 'Survey';
  }
  if (options.prefixContainer) {
    options.prefixContainer.innerHTML = renderSurveyPrefix(surveyItem, markdownRenderer, normalizedSurveyLocale);
    options.prefixContainer.classList.toggle('hidden', !options.prefixContainer.innerHTML.trim());
  }

  const survey = new Model(preparedSurveyJson);
  survey.locale = normalizedSurveyLocale === 'en' ? '' : normalizedSurveyLocale;
  survey.applyTheme(themeJson);
  bindSurveyFileUploads(survey);

  const storage = getStorage();
  const restoredState = loadSurveyState(storage, slug);
  survey.data = restoredState.data;
  survey.currentPageNo = restoredState.currentPageNo;

  applyTrackingDataToSurvey(survey, getTrackingData(), false);

  const trackingBridge = createTrackingBridge({
    windowObject: window,
    updateTrackingDataFn: updateTrackingData,
    onTrackingData: (trackingData) => {
      applyTrackingDataToSurvey(survey, trackingData, true);
    },
  });

  const persistState = () => {
    saveSurveyState(storage, slug, {
      currentPageNo: survey.currentPageNo,
      data: survey.data,
    });
  };

  survey.onValueChanged.add(() => {
    persistState();
  });
  survey.onCurrentPageChanged.add(() => {
    persistState();
  });
  survey.onPartialSend.add(() => {
    persistState();
  });
  survey.onComplete.add((surveyInstance, completeOptions) => {
    persistState();
    void (async () => {
      completeOptions.showDataSaving('Submitting your response... Please wait.');

      let trackingData = collectTrackingPayload({
        survey: surveyInstance,
        trackingData: getTrackingData(),
      });
      if (!hasAnyTrackingData(trackingData)) {
        await trackingBridge.requestTrackingFromParent();
        trackingData = collectTrackingPayload({
          survey: surveyInstance,
          trackingData: getTrackingData(),
        });
      }

      const payload = buildSurveySubmitPayload({
        surveyData: surveyInstance.data,
        browserId: userBrowserId,
        trackingData,
        clientContext: buildSurveyClientContext(window),
      });

      try {
        const response = await fetch(`${BACKEND_URL}/api/surveys/${encodeURIComponent(slug)}/submit`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json; charset=utf-8',
          },
          body: JSON.stringify(payload),
        });
        if (response.status !== 201) {
          throw new Error(`Server responded with ${response.status}`);
        }
        clearSurveyState(storage, slug);
        runSurveyPostSubmitTracking(slug);
      } catch (error) {
        console.error('Failed to submit survey', error);
        completeOptions.showDataSavingError(
          'Oops! Something went wrong. Your response is still saved in this browser, so you can try submitting again later.',
        );
      }
    })();
  });

  survey.render(formContainer);
  setStatus(options.statusContainer, '');
  initIframeResize();

  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', () => {
      trackingBridge.dispose();
    }, {once: true});
  }

  return survey;
}
